import { NavLink } from "react-router-dom";
import { LayoutDashboard, BookOpen, Wallet, Copy, LogOut, Activity } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { SessionClock } from "@/components/SessionClock";

const NAV = [
  { to: "/", label: "Dashboard", icon: LayoutDashboard, id: "dashboard" },
  { to: "/journal", label: "Journal", icon: BookOpen, id: "journal" },
  { to: "/accounts", label: "Accounts", icon: Wallet, id: "accounts" },
  { to: "/copy-trading", label: "Copy Trading", icon: Copy, id: "copy-trading" },
];

const linkCls = ({ isActive }) =>
  `flex items-center gap-3 px-3 py-2 rounded-md text-sm transition-colors ${isActive ? "bg-emerald-500/10 text-emerald-400 border border-emerald-500/20" : "text-slate-400 hover:text-slate-100 hover:bg-slate-800/50 border border-transparent"}`;

export const Layout = ({ children }) => {
  const { user, logout } = useAuth();
  return (
    <div className="min-h-screen flex">
      <aside className="hidden md:flex w-60 flex-col border-r border-slate-800/80 bg-[#0D1119] p-4 sticky top-0 h-screen">
        <div className="flex items-center gap-2 px-2 mb-8">
          <Activity className="h-5 w-5 text-emerald-400" />
          <span className="font-display font-bold text-lg tracking-tight">TradePulse</span>
        </div>
        <nav className="flex flex-col gap-1">
          {NAV.map((n) => (
            <NavLink key={n.to} to={n.to} end className={linkCls} data-testid={`nav-${n.id}-link`}>
              <n.icon className="h-4 w-4" />{n.label}
            </NavLink>
          ))}
        </nav>
        <div className="mt-auto border-t border-slate-800 pt-4">
          <p className="text-xs text-slate-500 font-mono truncate px-2" data-testid="current-user-email">{user?.email}</p>
          <button onClick={logout} data-testid="logout-button" className="mt-2 flex items-center gap-2 px-2 py-1.5 text-sm text-slate-400 hover:text-red-400 transition-colors">
            <LogOut className="h-4 w-4" /> Sign out
          </button>
        </div>
      </aside>
      <div className="flex-1 min-w-0 flex flex-col">
        <header className="h-14 sticky top-0 z-30 flex items-center justify-between gap-3 px-4 md:px-8 border-b border-slate-800/80 bg-[#0B0E14]/85 backdrop-blur-md">
          <span className="md:hidden font-display font-bold flex items-center gap-2"><Activity className="h-4 w-4 text-emerald-400" />TradePulse</span>
          <SessionClock />
          <button onClick={logout} data-testid="logout-button-mobile" className="md:hidden text-slate-400"><LogOut className="h-4 w-4" /></button>
        </header>
        <main className="flex-1 px-4 md:px-8 py-6 pb-24 md:pb-8 max-w-[1400px] w-full">{children}</main>
        <nav className="md:hidden fixed bottom-0 inset-x-0 z-30 grid grid-cols-4 border-t border-slate-800 bg-[#0D1119]">
          {NAV.map((n) => (
            <NavLink key={n.to} to={n.to} end data-testid={`mobile-nav-${n.id}-link`}
              className={({ isActive }) => `flex flex-col items-center py-2 text-[10px] ${isActive ? "text-emerald-400" : "text-slate-500"}`}>
              <n.icon className="h-4 w-4 mb-0.5" />{n.label}
            </NavLink>
          ))}
        </nav>
      </div>
    </div>
  );
};

export const PageHeader = ({ title, subtitle, children }) => (
  <div className="flex flex-col sm:flex-row sm:items-end justify-between gap-4 mb-8 rise">
    <div>
      <h1 className="font-display text-3xl sm:text-4xl font-bold tracking-tight">{title}</h1>
      {subtitle && <p className="text-sm text-slate-400 mt-1">{subtitle}</p>}
    </div>
    <div className="flex flex-wrap gap-2">{children}</div>
  </div>
);
